import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import {
  getInventoryRulesData,
  getInventoryRulesFilters,
  IInventoryRule,
  IInventoryRulesQueryParams,
} from '~/src/dash/apiV2/inventory-rules/inventoryRules';

import { IStartToEndDateRanges, validateDateRanges } from '~/src/dash/components/calendar/common';
import { SearchResultsText } from '~/src/dash/components/input/common';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractDefaultActiveColumnKeys,
  extractVisibleColumns,
  IListViewTableColumnInfo,
  IListViewTableRow,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { ColumnSelector } from '~/src/dash/components/tables/ListViewTable/linked_components/ColumnSelector';
import {
  Col,
  Cols,
  Panel,
  PanelBreak,
  Panels,
  Row,
  Rows,
  spacing,
  TextInput,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { isSameMoment } from '~/src/dash/utils/calendar';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import { formatInventoryRulesResponseIntoLVTRows } from '~/src/dash/views/inventory-rules-v2/Rules/InventoryRulesList/config';
import { DateRange } from '~/src/dash/views/inventory-rules-v2/Rules/InventoryRulesList/DateRange';
import { SnackbarView } from '~/src/dash/views/snackbar-view';
import { InventoryRuleDrawer } from '~/src/dash/views/inventory-rules-v2/Rules/InventoryRulesList/InventoryRuleDrawer';
import {
  generateOptionsListAsPromise,
  ISelectOption,
} from '~/src/dash/components/dropdowns/common';
import { RuleTypeOptions } from '~/src/dash/views/inventory-rules-v2/common';
import { Filters } from '~/src/dash/views/inventory-rules-v2/Rules/InventoryRulesList/Filters';
import { formatSelectedFiltersIntoQueryObject } from '~/src/dash/components/filters/conversions';
import { publicaColors } from '~/src/dash/utils/colors';

const LABEL_WIDTH = 96;
const LABEL_HEIGHT = 36;

export const RulesList = (): JSX.Element => {
  const { isSuperUser, session } = useBaseStore();
  const { t } = usePublicaTranslation();
  const { InventoryRules } = useBaseConfig();
  const cfg = InventoryRules.Rules.All;
  // date ranges
  const [startToEndDateRanges, setStartToEndDateRanges] = useState<IStartToEndDateRanges>(
    cfg.startToEndDateRanges,
  );
  const [areDateRangesInvalid, setAreDateRangesInvalid] = useState(false);
  // filters
  const [filtersLoadState, setFiltersLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [filters, setFilters] = useState<Record<string, ISelectOption[]>>({});
  const [selectedFilters, setSelectedFilters] = useState<Record<string, Set<string | number>>>({});
  // table
  const [activeColumnKeys, setActiveColumnKeys] = useState<string[]>(
    cfg.activeColumnKeys || extractDefaultActiveColumnKeys(cfg.columns),
  );

  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [responseData, setResponseData] = useState<IInventoryRule[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);

  const [query, setQuery] = useState('');

  const [order, setOrder] = useState<Order>(cfg.defaultState.orderDirection || 'asc');
  const [orderBy, setOrderBy] = useState<string>((cfg.defaultState.orderBy as string) || 'ID');
  const [page, setPage] = useState<number>(cfg.defaultState.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(cfg.defaultState.rowsPerPage || 10);
  // AbortController ref for table requests
  const tableAbortControllerRef = useRef<AbortController | null>(null);
  // drawer
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerIndex, setDrawerIndex] = useState<number | null>(null);
  // snackbar
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);

  const isMounted = useRef(false);

  const handleApplyDateRanges = (ranges: IStartToEndDateRanges): void => {
    const { startDateFrom, startDateTo, endDateFrom, endDateTo } = ranges;
    // Only update state if the values are different
    const hasChanged =
      !isSameMoment(startToEndDateRanges.startDateFrom, startDateFrom) ||
      !isSameMoment(startToEndDateRanges.startDateTo, startDateTo) ||
      !isSameMoment(startToEndDateRanges.endDateFrom, endDateFrom) ||
      !isSameMoment(startToEndDateRanges.endDateTo, endDateTo);

    if (hasChanged) {
      // validate date ranges
      const { isValid, errorTKey, errorTProps } = validateDateRanges(ranges);
      if (!isValid) {
        unstable_batchedUpdates(() => {
          setSnackbarMessage(t(errorTKey, errorTProps));
          setSnackbarError(true);
          setAreDateRangesInvalid(true);
        });
      } else {
        unstable_batchedUpdates(() => {
          setStartToEndDateRanges(ranges);
          setAreDateRangesInvalid(false);
          setPage(0);
        });
        cfg.startToEndDateRanges = ranges;
        cfg.table.page = 0;
      }
    } else if (!startDateFrom && !startDateTo && !endDateFrom && !endDateTo) {
      setAreDateRangesInvalid(false);
    }
  };

  // filter functions
  const handleFilterAccept = (value: Set<number | string>, key: string): void => {
    if (
      selectedFilters[key].size === value.size &&
      [...selectedFilters[key]].every((item) => value.has(item))
    ) {
      // No change, do nothing
      return;
    }

    const clonedSelectedFilters = { ...selectedFilters };
    clonedSelectedFilters[key] = value;
    unstable_batchedUpdates(() => {
      setSelectedFilters(clonedSelectedFilters);
      setPage(0);
    });
    cfg.filters[key].selectedOptionIDs = value;
    cfg.table.page = 0;
  };

  const handleClearFilters = (): void => {
    const clearedSelectedFilters: Record<string, Set<string | number>> = {};
    Object.values(cfg.filters).forEach((filter) => {
      const initialDefaultIDs = new Set<string | number>(filter.selectedOptionIDsOnReset || []);
      clearedSelectedFilters[filter.key] = initialDefaultIDs;
      cfg.filters[filter.key].selectedOptionIDs = initialDefaultIDs;
    });
    unstable_batchedUpdates(() => {
      setSelectedFilters(clearedSelectedFilters);
      setPage(0);
    });
    cfg.table.page = 0;
  };

  const handleOnChangeQuery = (text: string): void => {
    unstable_batchedUpdates(() => {
      setQuery(text);
      setPage(0);
    });
  };

  // TODO: add this if column config order is implemented
  // const columnOrder = Object.keys(cfg.columns);
  // const sortByColumnOrder = (keys: string[]): string[] =>
  //   keys.slice().sort((a, b) => columnOrder.indexOf(a) - columnOrder.indexOf(b));

  const handleChangeActiveColumns = (keys: string[]): void => {
    setActiveColumnKeys(keys); // default behavior
    // setActiveColumnKeys(sortByColumnOrder(keys)); // sort by column config order
  };

  const handleAcceptActiveColumns = (keys: string[]): void => {
    // const sorted = sortByColumnOrder(keys);
    setActiveColumnKeys(keys); // default behavior
    cfg.activeColumnKeys = keys; // default behavior
    // setActiveColumnKeys(sorted); // sort by column config order
    // cfg.activeColumnKeys = sorted; // sort by column config order
  };

  // this sets up the changes to force a re-sort
  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo): void => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleChangePage = (_e: unknown, newPage: number): void => {
    setPage(newPage);
    cfg.table.page = newPage;
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(updatedRowsPerPage);
      setPage(0);
    });
  };

  // drawer functions
  const handleClickOpenDrawer = (row: IListViewTableRow): void => {
    const ruleId = row.ID?.value as string | number | undefined;
    const index = responseData.findIndex((rule) => rule.ID === Number(ruleId));

    if (index < 0) {
      unstable_batchedUpdates(() => {
        setSnackbarMessage(t('SnackbarNoDataFound'));
        setSnackbarError(true);
      });
      return;
    }

    unstable_batchedUpdates(() => {
      setIsDrawerOpen(true);
      setDrawerIndex(index);
    });
  };

  const populateFilters = (): void => {
    setFiltersLoadState(LoadingStateLoading);
    const tempFilters: Record<string, ISelectOption[]> = {};
    const tempSelectedFilters: Record<string, Set<string | number>> = {};

    Promise.all([
      generateOptionsListAsPromise(RuleTypeOptions),
      getInventoryRulesFilters(session.publisher.ID),
    ])
      .then(([ruleTypeOptions, inventoryRulesFilters]) => {
        const allFilterData: Record<string, ISelectOption[]> = {
          RuleType: ruleTypeOptions,
          ...inventoryRulesFilters,
        };

        Object.keys(cfg.filters).forEach((filterKey) => {
          tempFilters[filterKey] = allFilterData[filterKey] || [];
          tempSelectedFilters[filterKey] =
            cfg.filters[filterKey].selectedOptionIDs || new Set<string | number>();
        });

        unstable_batchedUpdates(() => {
          setFilters(tempFilters);
          setSelectedFilters(tempSelectedFilters);
          setFiltersLoadState(LoadingStateHasData);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setFilters({});
          setSelectedFilters({});
          setFiltersLoadState(LoadingStateNoData);
          setSnackbarError(true);
          setSnackbarMessage(t('SnackbarErrorRetrievingItem', { item: t('Filters') }));
        });
      });
  };

  const fetchTableData = useCallback((): void => {
    if (tableAbortControllerRef.current) {
      tableAbortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    tableAbortControllerRef.current = new AbortController();
    const currentController = tableAbortControllerRef.current;

    setLoadState(LoadingStateLoading);

    // to build filter params here
    const filterParams = formatSelectedFiltersIntoQueryObject(selectedFilters, cfg.filters);

    // Build full table query params
    const tableQuery = {
      Search: query,
      StartDateFrom: startToEndDateRanges.startDateFrom?.format('YYYY-MM-DD') || '',
      StartDateTo: startToEndDateRanges.startDateTo?.format('YYYY-MM-DD') || '',
      EndDateFrom: startToEndDateRanges.endDateFrom?.format('YYYY-MM-DD') || '',
      EndDateTo: startToEndDateRanges.endDateTo?.format('YYYY-MM-DD') || '',
      Page: page,
      PageSize: rowsPerPage,
      OrderBy: orderBy,
      OrderDirection: order,
      ...filterParams,
    } as IInventoryRulesQueryParams;

    getInventoryRulesData(session.publisher.ID, tableQuery, currentController.signal)
      .then((response) => {
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateHasData);
          setResponseData(response.InventoryRules || []);
          setTotalRowCount(response.TotalCount || 0);
        });
      })
      .catch((err: Error) => {
        // Ignore abort errors (they're expected when cancelling requests)
        if (err.name === 'AbortError') {
          return;
        }
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateNoData);
          setResponseData([]);
          setTotalRowCount(0);
        });
        console.error(err);
      });
  }, [
    session.publisher.ID,
    startToEndDateRanges,
    order,
    orderBy,
    rowsPerPage,
    page,
    query,
    selectedFilters,
  ]);

  useEffect(() => {
    populateFilters();
  }, []);

  // Fetch table data when filters or pagination change
  useEffect(() => {
    if (isMounted.current) {
      fetchTableData();
    }
  }, [fetchTableData]);

  // Cleanup: abort any in-flight requests when component unmounts
  useEffect(() => {
    isMounted.current = true;
    return () => {
      if (tableAbortControllerRef.current) {
        tableAbortControllerRef.current.abort();
      }
    };
  }, []);

  const activeColumnsList = useMemo(
    () => extractVisibleColumns(cfg.columns, activeColumnKeys, isSuperUser),
    [],
  );

  const rows = useMemo(
    () => formatInventoryRulesResponseIntoLVTRows(responseData, cfg.columns),
    [responseData, cfg.columns],
  );

  return (
    <Panels
      max
      vertical
      relative
      largeHorizontalPadding
      verticalPadding
      style={{ rowGap: spacing.medium }}
    >
      <Panel highlighted>
        <Rows>
          <Row
            extraSmallVerticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <DateRange
              cypressParentName={cfg.cypressClassName}
              selectedRanges={startToEndDateRanges}
              handleApplyDateRanges={handleApplyDateRanges}
              hasError={areDateRangesInvalid}
              labelWidth={LABEL_WIDTH}
              labelHeight={LABEL_HEIGHT}
            />
          </Row>
          <Row noVerticalPadding>
            <Filters
              cypressParentName={cfg.cypressClassName}
              labelWidth={LABEL_WIDTH}
              labelHeight={LABEL_HEIGHT}
              enableClear={true}
              filtersConfig={cfg.filters}
              filtersToOptionsMap={filters}
              handleClearFilters={handleClearFilters}
              handleApplySelect={handleFilterAccept}
              selectedFilters={selectedFilters}
              loadState={filtersLoadState}
            />
          </Row>
        </Rows>
      </Panel>
      <Panel highlighted basePadding>
        <Rows>
          <Row noPadding>
            <Cols style={{ justifyContent: 'space-between' }}>
              <Col>
                <Cols
                  wrap
                  gap={spacing.medium}
                  style={{
                    alignItems: 'center',
                    rowGap: spacing.small,
                  }}
                >
                  <Col>
                    <Cols
                      wrap
                      gap={spacing.medium}
                      style={{
                        alignItems: 'center',
                        rowGap: spacing.small,
                      }}
                    >
                      <Col>
                        <TextInput
                          enableClear
                          handleOnChange={handleOnChangeQuery}
                          msDelay={500}
                          placeholder={t('SearchByName')}
                          prefix='search'
                        />
                      </Col>
                      <Col>
                        <SearchResultsText
                          numRowsDisplayed={responseData.length}
                          numTotalRows={totalRowCount}
                        />
                      </Col>
                    </Cols>
                  </Col>
                </Cols>
              </Col>
              <Col>
                <ColumnSelector
                  preselectedColumnKeys={activeColumnKeys}
                  resetActiveColumnKeys={cfg.columnSelector.resetKeys}
                  columnsList={activeColumnsList}
                  columnsHandleOnChangeOption={handleChangeActiveColumns}
                  columnsHandleOnAccept={handleAcceptActiveColumns}
                  // disableSort // TODO: add this if column config order is implemented
                />
              </Col>
            </Cols>
          </Row>
          <Row noHorizontalPadding verticalPadding>
            <ListViewTable
              activeColumnKeys={activeColumnKeys}
              columns={cfg.columns}
              // getItemSettingsRoute={getLineItemSettingsRoute} // TODO: add this once object views are completed
              handleOnClickRow={handleClickOpenDrawer}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleOnClickColumnHeader={handleOnClickColumnHeader}
              isReadAuthorized={true}
              isSuperUser={isSuperUser}
              isWriteAuthorized={true}
              loadState={loadState}
              order={order}
              orderBy={orderBy}
              page={page}
              rows={rows}
              rowsPerPage={rowsPerPage}
              tableName={'Inventory Rules'}
              totalRowCount={totalRowCount}
              className={cfg.cypressClassName}
            />
          </Row>
        </Rows>
      </Panel>
      <InventoryRuleDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        currentIndex={drawerIndex}
        tableData={responseData}
        loadState={loadState}
        cfg={cfg}
      />
      <SnackbarView
        open={!!snackbarMessage}
        error={!!snackbarError}
        onClose={() => {
          unstable_batchedUpdates(() => {
            setSnackbarMessage('');
            setSnackbarError(false);
          });
        }}
      >
        {snackbarMessage}
      </SnackbarView>
      <PanelBreak height={spacing.large} />
    </Panels>
  );
};
